import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { type ForgotPasswordPayload, forgotPasswordSchema, useForgotPassword } from 'src/features/auth/api';
import { commonMessages, forgotPasswordMessages, loginMessages } from 'src/features/auth/utils';
import FormErrorBlock from 'src/registry/components/blocks/FormErrorBlock';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';

const ForgotPasswordForm = () => {
    const { formatMessage } = useIntl();
    const [success, setSuccess] = React.useState(false);

    const {
        mutate: forgotPassword,
        error,
        isPending,
    } = useForgotPassword({
        onSuccess: () => {
            setSuccess(true);
        },
    });

    const form = useForm<ForgotPasswordPayload>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = (values: ForgotPasswordPayload) => {
        forgotPassword(values);
    };

    return (
        <>
            <div className="flex w-full max-w-sm flex-col items-center py-6 text-center">
                <h1 className="text-2xl font-semibold">{formatMessage(forgotPasswordMessages.forgotPassword)}</h1>
                <h3 className="text-center text-gray-500">
                    {formatMessage(forgotPasswordMessages.forgotPasswordMessage)}
                </h3>
            </div>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                {!success && (
                    <>
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{formatMessage(commonMessages.email)}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            placeholder={formatMessage(commonMessages.emailPlaceholder)}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage>
                                        <FormattedMessage {...forgotPasswordMessages.forgotPasswordEmailTip} />
                                    </FormMessage>
                                </FormItem>
                            )}
                        />
                        <Button className="w-full" type="submit" disabled={isPending || !form.formState.isValid}>
                            {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 animate-spin" />}
                            {formatMessage(generalMessages.submit)}
                        </Button>
                        <FormErrorBlock error={error} />
                    </>
                )}
                {success && (
                    <div className="flex flex-col gap-1 text-center text-sm">
                        <p className="font-semibold">{formatMessage(forgotPasswordMessages.forgotPasswordSuccess)}</p>
                        <p className="text-gray-500">
                            {formatMessage(forgotPasswordMessages.forgotPasswordSuccessDetails)}
                        </p>
                    </div>
                )}
                <div className="relative mt-4 flex flex-col gap-6">
                    <p className="absolute inset-x-0 -top-3 z-20 mx-auto w-12 bg-white p-1 text-center text-xs text-gray-500 uppercase">
                        <FormattedMessage {...generalMessages.or} />
                    </p>
                    <Separator />
                    <Link
                        to="/login"
                        className="text-primary-700 hover:text-primary-600 self-center text-center text-sm hover:underline"
                    >
                        <FormattedMessage {...loginMessages.loginMessage} />
                    </Link>
                </div>
            </Form>
        </>
    );
};

export { ForgotPasswordForm };
